// src/components/pdf-viewer/AnnotationSidebar.tsx
import React, { useState, useMemo } from 'react';
import {
  List,
  Card,
  Tag,
  Button,
  Input,
  Empty,
  Space,
  Popconfirm,
  Typography,
  Divider,
  Badge,
  Checkbox,
  Modal,
  Spin,
  Result,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  FileTextOutlined,
  TagsOutlined,
  UserOutlined,
  PlusOutlined,
  FileOutlined,
  FileWordOutlined,
  DownloadOutlined,
  LoadingOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import type { Annotation } from '../../types/annotation';
import { VoiceSearchButton } from './VoiceSearchButton';

const { Text, Paragraph } = Typography;
const { Search } = Input;

interface AnnotationSidebarProps {
  annotations: Annotation[];
  currentPage: number;
  onSelectAnnotation: (annotation: Annotation) => void;
  onEditAnnotation: (annotation: Annotation) => void;
  onDeleteAnnotation: (annotationId: string) => void;
  onAddGeneralNote?: () => void;
  enableWorkOrder?: boolean;
  onGenerateWorkOrder?: (annotationIds: string[]) => Promise<Blob | null>;
  workOrderLoading?: boolean;
}

export const AnnotationSidebar: React.FC<AnnotationSidebarProps> = ({
  annotations,
  currentPage,
  onSelectAnnotation,
  onEditAnnotation,
  onDeleteAnnotation,
  onAddGeneralNote,
  enableWorkOrder,
  onGenerateWorkOrder,
  workOrderLoading,
}) => {
  const [searchText, setSearchText] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [workOrderModalVisible, setWorkOrderModalVisible] = useState(false);
  const [workOrderGenerating, setWorkOrderGenerating] = useState(false);
  const [workOrderBlob, setWorkOrderBlob] = useState<Blob | null>(null);
  const [workOrderError, setWorkOrderError] = useState<string | null>(null);

  // Get all unique tags from annotations
  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    annotations.forEach(annotation => {
      annotation.tags.forEach(tag => tagSet.add(tag));
    });
    return Array.from(tagSet).sort();
  }, [annotations]);

  // Filter annotations by search text and selected tags
  const filteredAnnotations = useMemo(() => {
    return annotations.filter(annotation => {
      // Filter by search text
      if (searchText) {
        const searchLower = searchText.toLowerCase();
        const matchesComment = annotation.comment.toLowerCase().includes(searchLower);
        const matchesTags = annotation.tags.some(tag =>
          tag.toLowerCase().includes(searchLower)
        );
        if (!matchesComment && !matchesTags) {
          return false;
        }
      }

      // Filter by selected tags
      if (selectedTags.length > 0) {
        const hasSelectedTag = selectedTags.some(tag =>
          annotation.tags.includes(tag)
        );
        if (!hasSelectedTag) {
          return false;
        }
      }

      return true;
    });
  }, [annotations, searchText, selectedTags]);

  // Group annotations by page
  const annotationsByPage = useMemo(() => {
    const grouped: { [page: number]: Annotation[] } = {};
    filteredAnnotations.forEach(annotation => {
      if (!grouped[annotation.page]) {
        grouped[annotation.page] = [];
      }
      grouped[annotation.page].push(annotation);
    });
    return grouped;
  }, [filteredAnnotations]);

  // Sort pages
  const sortedPages = useMemo(() => {
    return Object.keys(annotationsByPage)
      .map(Number)
      .sort((a, b) => a - b);
  }, [annotationsByPage]);

  const handleTagClick = (tag: string) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter(t => t !== tag));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  const handleClearFilters = () => {
    setSearchText('');
    setSelectedTags([]);
  };

  const handleToggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === filteredAnnotations.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredAnnotations.map(a => a.id)));
    }
  };

  const handleGenerateWorkOrder = async () => {
    if (onGenerateWorkOrder && selectedIds.size > 0) {
      setWorkOrderBlob(null);
      setWorkOrderError(null);
      setWorkOrderGenerating(true);
      setWorkOrderModalVisible(true);
      try {
        // Send IDs in the SAME order they appear in the sidebar (grouped by
        // page ascending, then display order) so the generated document matches
        // the on-screen order rather than the order checkboxes were clicked.
        const orderedIds = sortedPages
          .flatMap((page) => annotationsByPage[page] || [])
          .filter((a) => selectedIds.has(a.id))
          .map((a) => a.id);
        const blob = await onGenerateWorkOrder(orderedIds);
        if (blob) {
          setWorkOrderBlob(blob);
        } else {
          setWorkOrderError('No data returned from server.');
        }
      } catch (err: any) {
        setWorkOrderError(err.message || 'Failed to generate work order.');
      } finally {
        setWorkOrderGenerating(false);
      }
    }
  };

  const handleDownloadWorkOrder = () => {
    if (workOrderBlob) {
      const url = window.URL.createObjectURL(workOrderBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Repair_Request_${new Date().toISOString().split('T')[0]}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }
  };

  const handleCloseWorkOrderModal = () => {
    setWorkOrderModalVisible(false);
    setWorkOrderBlob(null);
    setWorkOrderError(null);
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Side panel styles - sits beside the PDF, not overlaying it
  const sidebarStyle: React.CSSProperties = {
    width: '320px',
    minWidth: '320px',
    height: '100%',
    backgroundColor: '#fff',
    borderLeft: '1px solid #e8e8e8',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  };

  const headerStyle: React.CSSProperties = {
    padding: '12px 16px',
    borderBottom: '1px solid #e8e8e8',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#fafafa',
  };

  const bodyStyle: React.CSSProperties = {
    flex: 1,
    padding: '12px',
    overflowY: 'auto',
  };

  return (
    <div style={sidebarStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <Space>
          <FileTextOutlined />
          <span style={{ fontWeight: 600 }}>Comments</span>
          <Badge count={annotations.length} showZero />
        </Space>
        <Space size={4}>
          {enableWorkOrder && filteredAnnotations.length > 0 && (
            <Checkbox
              checked={selectedIds.size > 0 && selectedIds.size === filteredAnnotations.length}
              indeterminate={selectedIds.size > 0 && selectedIds.size < filteredAnnotations.length}
              onChange={handleSelectAll}
              style={{ fontSize: '12px' }}
            >
              <span style={{ fontSize: '11px' }}>All</span>
            </Checkbox>
          )}
          {onAddGeneralNote && (
            <Button
              type="link"
              size="small"
              icon={<PlusOutlined />}
              onClick={onAddGeneralNote}
              style={{ padding: '0 4px', height: 'auto', fontSize: '12px' }}
            >
              General Note
            </Button>
          )}
        </Space>
      </div>

      {/* Body */}
      <div style={bodyStyle}>
      {/* Search and Filter Section */}
      <div style={{ marginBottom: '16px' }}>
        <Search
          placeholder="Search comments or tags"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          prefix={<SearchOutlined />}
          suffix={<VoiceSearchButton onResult={setSearchText} />}
          allowClear
          style={{ marginBottom: '12px' }}
        />

        {allTags.length > 0 && (
          <div>
            <div style={{
              fontSize: '12px',
              color: '#666',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <Space size={4}>
                <TagsOutlined />
                <span>Filter by tags</span>
              </Space>
              {selectedTags.length > 0 && (
                <Button
                  type="link"
                  size="small"
                  onClick={handleClearFilters}
                  style={{ padding: 0, height: 'auto' }}
                >
                  Clear
                </Button>
              )}
            </div>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '6px',
            }}>
              {allTags.map(tag => (
                <Tag
                  key={tag}
                  color={selectedTags.includes(tag) ? 'blue' : 'default'}
                  style={{
                    cursor: 'pointer',
                    margin: 0,
                  }}
                  onClick={() => handleTagClick(tag)}
                >
                  {tag}
                </Tag>
              ))}
            </div>
          </div>
        )}

        {(searchText || selectedTags.length > 0) && (
          <div style={{
            marginTop: '8px',
            fontSize: '12px',
            color: '#666',
          }}>
            Showing {filteredAnnotations.length} of {annotations.length} annotations
          </div>
        )}
      </div>

      <Divider style={{ margin: '12px 0' }} />

      {/* Annotations List */}
      {filteredAnnotations.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            searchText || selectedTags.length > 0
              ? 'No annotations match your filters'
              : 'No annotations yet'
          }
          style={{ marginTop: '40px' }}
        >
          {(searchText || selectedTags.length > 0) && (
            <Button type="primary" size="small" onClick={handleClearFilters}>
              Clear Filters
            </Button>
          )}
        </Empty>
      ) : (
        <div style={{
          height: 'calc(100vh - 240px)',
          overflowY: 'auto',
        }}>
          {sortedPages.map(page => (
            <div key={page} style={{ marginBottom: '16px' }}>
              {/* Page Header */}
              <div style={{
                padding: '6px 12px',
                backgroundColor: page === 0 ? '#f6ffed' : page === currentPage ? '#e6f7ff' : '#f5f5f5',
                borderRadius: '4px',
                marginBottom: '8px',
                fontSize: '13px',
                fontWeight: 600,
                color: page === 0 ? '#52c41a' : page === currentPage ? '#1890ff' : '#666',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}>
                {page === 0 && <FileOutlined />}
                {page === 0 ? 'General Notes' : `Page ${page}`}
                <Badge
                  count={annotationsByPage[page].length}
                  style={{
                    backgroundColor: page === 0 ? '#52c41a' : page === currentPage ? '#1890ff' : '#999',
                    marginLeft: '8px',
                  }}
                />
              </div>

              {/* Annotations for this page */}
              <List
                dataSource={annotationsByPage[page]}
                renderItem={(annotation) => (
                  <Card
                    size="small"
                    style={{
                      marginBottom: '8px',
                      cursor: annotation.type === 'general' ? 'default' : 'pointer',
                      borderLeft: `4px solid ${annotation.color}`,
                      boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                    }}
                    hoverable={annotation.type !== 'general'}
                    onClick={() => annotation.type !== 'general' && onSelectAnnotation(annotation)}
                    bodyStyle={{ padding: '10px' }}
                  >
                    {/* Header row with author and action buttons */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '6px',
                    }}>
                      {/* Checkbox + Author name */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        flex: 1,
                        minWidth: 0,
                      }}>
                        {enableWorkOrder && (
                          <Checkbox
                            checked={selectedIds.has(annotation.id)}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleToggleSelect(annotation.id);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            style={{ marginRight: '2px' }}
                          />
                        )}
                        <UserOutlined style={{ fontSize: '12px', color: '#1890ff' }} />
                        <Text
                          strong
                          style={{
                            fontSize: '12px',
                            color: '#1890ff',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {annotation.authorName || annotation.authorEmail || 'Unknown'}
                        </Text>
                      </div>
                      {/* Action buttons */}
                      <Space size={4} style={{ flexShrink: 0 }}>
                        <Button
                          type="text"
                          size="small"
                          icon={<EditOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditAnnotation(annotation);
                          }}
                          style={{ padding: '2px 4px', height: 'auto' }}
                        />
                        <Popconfirm
                          title="Delete comment?"
                          description="This action cannot be undone."
                          onConfirm={(e) => {
                            e?.stopPropagation();
                            onDeleteAnnotation(annotation.id);
                          }}
                          okText="Delete"
                          cancelText="Cancel"
                          okButtonProps={{ danger: true }}
                          onCancel={(e) => e?.stopPropagation()}
                        >
                          <Button
                            type="text"
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={(e) => e.stopPropagation()}
                            style={{ padding: '2px 4px', height: 'auto' }}
                          />
                        </Popconfirm>
                      </Space>
                    </div>

                    {/* Comment text - show prominently, or show "No comment" if empty */}
                    <Paragraph
                      ellipsis={{ rows: 3, expandable: false }}
                      style={{
                        fontSize: '13px',
                        marginBottom: '6px',
                        color: annotation.comment ? '#333' : '#999',
                        fontStyle: annotation.comment ? 'normal' : 'italic',
                      }}
                    >
                      {annotation.comment || 'No comment added'}
                    </Paragraph>

                    {/* Tags */}
                    {annotation.tags.length > 0 && (
                      <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '4px',
                        marginBottom: '6px',
                      }}>
                        {annotation.tags.map(tag => (
                          <Tag
                            key={tag}
                            color="blue"
                            style={{
                              fontSize: '11px',
                              margin: 0,
                              padding: '0 6px',
                              lineHeight: '18px',
                            }}
                          >
                            {tag}
                          </Tag>
                        ))}
                      </div>
                    )}

                    {/* Timestamp */}
                    <Text
                      type="secondary"
                      style={{ fontSize: '11px' }}
                    >
                      {formatDate(annotation.createdAt)}
                    </Text>
                  </Card>
                )}
              />
            </div>
          ))}
        </div>
      )}
      </div>

      {/* Generate Work Order button - sticky at bottom */}
      {enableWorkOrder && selectedIds.size > 0 && (
        <div style={{
          padding: '12px 16px',
          borderTop: '1px solid #e8e8e8',
          backgroundColor: '#fafafa',
        }}>
          <Button
            type="primary"
            icon={<FileWordOutlined />}
            onClick={handleGenerateWorkOrder}
            block
          >
            Generate Repair Request ({selectedIds.size})
          </Button>
          <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 6, textAlign: 'center' }}>
            Boxed photo comments are included as images.
          </div>
        </div>
      )}

      {/* Work Order Progress Modal */}
      <Modal
        title="Repair Request"
        open={workOrderModalVisible}
        onCancel={handleCloseWorkOrderModal}
        footer={null}
        centered
        maskClosable={false}
      >
        {workOrderGenerating ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
            <div style={{ marginTop: '24px', fontSize: '16px', color: '#333' }}>
              Generating Repair Request...
            </div>
            <div style={{ marginTop: '8px', fontSize: '13px', color: '#999' }}>
              AI is analyzing annotations and creating work items
            </div>
          </div>
        ) : workOrderError ? (
          <Result
            status="error"
            title="Generation Failed"
            subTitle={workOrderError}
            extra={
              <Button onClick={handleCloseWorkOrderModal}>Close</Button>
            }
          />
        ) : workOrderBlob ? (
          <Result
            icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
            title="Repair Request Ready"
            subTitle="Your AI-generated repair request is ready to download."
            extra={[
              <Button
                type="primary"
                key="download"
                icon={<DownloadOutlined />}
                onClick={handleDownloadWorkOrder}
                size="large"
              >
                Download Repair Request
              </Button>,
              <Button key="close" onClick={handleCloseWorkOrderModal}>
                Close
              </Button>,
            ]}
          />
        ) : null}
      </Modal>
    </div>
  );
};
